import { parseCreativeIdDigest } from 'hooks';
import type { CreativeEntry } from 'types/shared';
import type {
  CreatePinterestAdParams,
  PinterestAdGroupPayload,
  PinterestCampaignWithAdGroups,
  PinterestCreativePayload,
  PinterestCreativeType,
} from '../api/types';

export interface BuildCreatePinterestAdPayloadArgs {
  app: string;
  creativesRows: readonly CreativeEntry[];
  adGroupsData: readonly PinterestCampaignWithAdGroups[];
  selectedAdGroups: readonly string[];
}

const toCreativeType = (value: string): PinterestCreativeType =>
  value.toLowerCase() === 'image' ? 'image' : 'video';

export const toPinterestCreativePayload = (row: CreativeEntry): PinterestCreativePayload => ({
  id: parseCreativeIdDigest(row.id) ?? row.id,
  creative_id: row.creative_id,
  creative_type: toCreativeType(row.creative_type),
  name: row.name,
});

export const toPinterestAdGroupPayloads = (
  adGroupsData: readonly PinterestCampaignWithAdGroups[],
  selectedAdGroups: readonly string[],
): PinterestAdGroupPayload[] => {
  const selected = new Set(selectedAdGroups);

  return adGroupsData.flatMap((campaign) =>
    campaign.ad_groups
      .filter((adGroup) => selected.has(adGroup.ad_group_id) && !!adGroup.flow)
      .map((adGroup) => ({
        account_id: campaign.account_id,
        campaign_id: campaign.campaign_id,
        campaign_name: campaign.campaign_name,
        ad_group_id: adGroup.ad_group_id,
        ad_group_name: adGroup.ad_group_name,
        flow: adGroup.flow!,
      })),
  );
};

export const buildCreatePinterestAdPayload = ({
  app,
  creativesRows,
  adGroupsData,
  selectedAdGroups,
}: BuildCreatePinterestAdPayloadArgs): CreatePinterestAdParams => ({
  app,
  creatives: creativesRows.map(toPinterestCreativePayload),
  ad_groups: toPinterestAdGroupPayloads(adGroupsData, selectedAdGroups),
});
